import { useState, type ReactElement } from "react";
import { Button, Icon } from "../../ui";
import { useBreakpoint } from "../../lib/useBreakpoint";
import styles from "./CredentialRow.module.css";

const MASK_LENGTH = 14;

export interface CredentialRowProps {
  label: string;
  value: string;
  secret?: boolean;
}

export function CredentialRow({
  label,
  value,
  secret = false,
}: CredentialRowProps): ReactElement {
  const { isMobile } = useBreakpoint();
  const [revealed, setRevealed] = useState(false);
  const [copyState, setCopyState] = useState<"idle" | "done" | "failed">("idle");

  const shown =
    secret && !revealed ? `${value.slice(0, MASK_LENGTH)}…` : value;

  async function copy() {
    // A rejected clipboard write is otherwise silent.
    try {
      await navigator.clipboard.writeText(value);
      setCopyState("done");
    } catch {
      setCopyState("failed");
    }
    setTimeout(() => setCopyState("idle"), 1500);
  }

  return (
    <div className={styles.row}>
      <span className={styles.label}>{label}</span>
      <div className={styles.value}>
        <span className={styles.mono}>{shown}</span>
        <div className={styles.actions}>
          {isMobile ? (
            <>
              {secret ? (
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => setRevealed((state) => !state)}
                >
                  {revealed ? "Hide" : "Reveal"}
                </Button>
              ) : null}
              <Button size="sm" variant="secondary" onClick={copy}>
                {copyState === "failed" ? "Failed" : copyState === "done" ? "Copied" : "Copy"}
              </Button>
            </>
          ) : null}
          {!isMobile && secret ? (
            <button
              type="button"
              className={styles.action}
              onClick={() => setRevealed((state) => !state)}
              aria-label={revealed ? `Hide ${label}` : `Reveal ${label}`}
            >
              <Icon name={revealed ? "eyeOff" : "eye"} size={18} />
            </button>
          ) : null}
          {isMobile ? null : (
          <button
            type="button"
            className={styles.action}
            onClick={copy}
            aria-label={
              copyState === "failed" ? `Could not copy ${label}` : `Copy ${label}`
            }
          >
            <Icon
              name={
                copyState === "done"
                  ? "check"
                  : copyState === "failed"
                    ? "alert"
                    : "copy"
              }
              size={18}
            />
          </button>
          )}
        </div>
      </div>
    </div>
  );
}
